<template>
  <div :class="$attrs.class">
    <div class="catering--flex-row-wrapper tw-flex tw-items-stretch tw-min-w-0 tw-bg-white tw-w-full tw-group tw-overflow-hidden 2xl:tw-rounded-3xl tw-px-4 md:tw-px-12 tw-mb-8">
      
      <div class="tw-hidden md:tw-flex md:tw-basis-2/5 md:tw-px-6 tw-pb-4 tw-flex-col tw-items-center tw-justify-start tw-min-w-0">
        <div class="tw-bg-white tw-shadow-lg tw-p-6 tw-border tw-border-gray-200 tw-w-full tw-flex tw-flex-col tw-items-center" style="border-radius: 4rem;">
          <div class="tw-relative tw-w-full tw-overflow-hidden tw-flex-1 tw-flex tw-items-center tw-justify-center tw-min-h-0">
              <div class="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-z-10" v-if="isLoading">
                <span class="fa fa-spinner tw-text-black tw-inline-block tw-animate-spin tw-ease tw-duration-300 tw-text-5xl"></span>
              </div>
              <img
                :alt="suggestion.title+' image'"
                :class="[
                  'tw-w-full tw-h-full tw-object-contain tw-object-center tw-transition-all tw-duration-300 tw-ease-out',
                  { 'tw-opacity-40': isLoading }
                ]"
                :src="productImage"
              />

          </div>

          <TwButton
            cssClass="tw-bg-orange-medium tw-border-2 tw-border-orange-medium tw-text-white
                      tw-font-bold tw-rounded-full tw-w-full
                      tw-flex tw-items-center tw-justify-center
                      tw-h-14 lg:tw-h-16
                      tw-leading-none tw-whitespace-nowrap
                      tw-text-base lg:tw-text-lg 2xl:tw-text-2xl
                      tw-px-6
                      hover:tw-bg-black hover:tw-border-black tw-transition-all tw-duration-200 tw-mt-6"
            @click="handleAdd"
            :isLoading="isLoading"
            :buttonText="addToBagText" />
        </div>
      </div>

      <div class="tw-w-full md:tw-basis-3/5 tw-px-4 md:tw-px-8 tw-text-left tw-min-w-0 tw-flex tw-flex-col md:tw-flex-row md:tw-items-stretch">
        <template v-if="isOldDescription">
          <div class="tw-flex tw-flex-1 md:tw-items-center">
            <div class="tw-w-5/6 md:tw-pt-0 tw-pt-6">
              <h3 class="tw-text-3xl tw-font-black tw-uppercase tw-pb-6">{{ suggestion.title }}</h3>
              <div class="tw-ml-0 tw-pl-0 tw-text-base sm:tw-text-lg md:tw-text-xl lg:tw-text-2xl tw-text-dark">
                {{ suggestion.description }}
              </div>
            </div>
          </div>
        </template>

        <template v-else>
          <div class="tw-w-full md:tw-pr-6 tw-mt-4 tw-leading-relaxed">
            <!-- Description -->
            <div class="tw-ml-0 tw-pl-0 tw-pb-4 tw-text-base sm:tw-text-lg md:tw-text-xl lg:tw-text-2xl tw-text-dark">
              {{ parsedDescription.text }}
            </div>

            <!-- Category groups -->
            <div v-for="group in parsedDescription.groups" :key="group.title" class="tw-mb-4">
              <h3 class="tw-font-bold tw-text-orange-medium tw-ml-0 tw-pl-0 tw-text-base sm:tw-text-lg md:tw-text-xl lg:tw-text-2xl">
                {{ group.title }}
              </h3>

              <ul class="tw-list-none tw-ml-0 tw-pl-0 tw-pb-4 tw-text-base sm:tw-text-lg md:tw-text-xl lg:tw-text-2xl">
                <li v-for="item in group.items" :key="item" class="tw-ml-0 tw-pl-0">
                  {{ item }}
                </li>
              </ul>
            </div>
          </div>
        </template>

        <div class="tw-mt-6 md:tw-hidden tw-pb-6">
          <TwButton
            cssClass="tw-bg-orange-medium tw-border-2 tw-border-orange-medium tw-text-white tw-font-bold tw-rounded-full tw-w-full tw-h-14 tw-flex tw-items-center tw-justify-center tw-leading-none tw-whitespace-nowrap tw-text-base tw-px-6 hover:tw-bg-black hover:tw-border-black tw-transition-all tw-duration-200"
            @click="handleAdd"
            :isLoading="isLoading"
            :buttonText="addToBagText" />
        </div>
      </div>

    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, onMounted, computed } from 'vue'
  import type CateringOrderSuggestion from '../../classes/CateringOrderSuggestion';
  import type Product from '../../classes/Product';
  import TwButton from '../../components/TwButton.vue';

  const props = defineProps<{ suggestion: CateringOrderSuggestion, index: number, isLoading: boolean, isDisabled: boolean }>();

  const productImage = ref('https://mypiada-assets.s3.amazonaws.com/static_assets/scooter-placeholder.jpg');

  const emit = defineEmits(['handleSuggestedOrderClick', 'handleSuggestedOrderModal'])

  const handleAdd = (event: Event) => {
    if (!props.isDisabled) {
      emit('handleSuggestedOrderClick', props.index)
    }
  }

  const isOldDescription = computed(() => {
    const raw = props.suggestion?.description;
    return !raw || !raw.includes('[') || !raw.includes(']');
  });

  const parsedDescription = computed(() => {
    const raw = props.suggestion?.description;

    if (!raw || typeof raw !== "string") {
      return { text: "", groups: [] };
    }

    // Description is text before []
    const textMatch = raw.match(/^(.*?)\[/);
    const textDescription = textMatch ? textMatch[1].trim() : raw.trim();

    // categories/items are inside []
    const arrayMatch = raw.match(/\[(.*?)\]/);
    if (!arrayMatch) {
      console.warn("No bracketed array found.");
      return { text: textDescription, groups: [] };
    }

    let itemsString = arrayMatch[1];

    // Split by comma into array
    const arr = itemsString
      .split(",")
      .map(s => s.trim())
      .filter(Boolean);

    const isCategoryTitle = (piece: string) => {
      const normalized = piece.trim().toLowerCase();
      return (
        normalized === 'entrees' ||
        normalized === 'sides' ||
        normalized === 'add-ons'
      );
    };

    // group items into their categories
    const groups: { title: string; items: string[] }[] = [];
    let currentGroup: { title: string; items: string[] } | null = null;

    arr.forEach(piece => {
      if (isCategoryTitle(piece)) {
        currentGroup = { title: piece, items: [] };
        groups.push(currentGroup);
      } else if (currentGroup) {
        currentGroup.items.push(piece);
      }
    });

    return {
      text: textDescription,
      groups
    };
  });


  const handleDetails = (event: Event) => {
    emit('handleSuggestedOrderModal', props.index)
  }

  const addToBagText = computed(() => {
    let str = 'Add to Bag - $';
    if (props.suggestion && props.suggestion.cost) {
      str += props.suggestion.cost!.toLocaleString("en-US", {minimumFractionDigits: 2})
    } else {
      str += '0.00'
    }

    return str;
  })

  // const suggestionDescription = computed(() => {
  //   let str = "";

  //   if (props.suggestion && props.suggestion.description) {
  //     str += props.suggestion.description;

  //     if (props.suggestion.products && props.suggestion.products.length > 0) {
  //       props.suggestion.products.forEach((product: Product, index: number) => {
  //         if (product.quantity > 1) {
  //           str += product.quantity + 'x ';
  //         }

  //         str += product.name;

  //         if (index < props.suggestion.products!.length - 1) {
  //           str += ", "
  //         }
  //       })
  //     }
  //   }

  //   return str;
  // })
</script>

<style scoped>
  .catering--flex-row-wrapper {
    display: flex;
  }
</style>
